/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Paper, Link, useTheme } from '@mui/material';
import { previewSlack, type SlackPreviewResponse } from '../../services/api';

/**
 * Token type for mrkdwn parsing
 */
type TokenType = 'text' | 'link' | 'bold' | 'italic' | 'strikethrough' | 'code' | 'codeBlock';

interface Token {
  type: TokenType;
  content: string;
  url?: string; // For links
  language?: string; // For code blocks
}

/**
 * Render Slack mrkdwn content for preview.
 * Converts Slack-specific formats to displayable HTML.
 * 
 * Supports:
 * - Links: <url|text>
 * - Bold: *text*
 * - Italic: _text_
 * - Strikethrough: ~text~
 * - Inline code: `code`
 * - Code blocks: ```language\ncode\n```
 * - Lists: • item, 1. item
 * - Blockquotes: > quote
 * - Horizontal rules: ──────────
 */
const MrkdwnRenderer: React.FC<{ content: string; theme: any }> = ({ content, theme }) => {
  /**
   * Parse a line into tokens, handling all mrkdwn formatting.
   * Processes in order: code blocks > links > code > bold > italic > strikethrough
   */
  const parseLine = (line: string): Token[] => {
    const tokens: Token[] = [];
    let currentIndex = 0;
    
    // First, handle code blocks (```...```)
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const codeBlockMatches: Array<{ index: number; length: number; language: string; code: string }> = [];
    let match;
    
    while ((match = codeBlockRegex.exec(line)) !== null) {
      codeBlockMatches.push({
        index: match.index,
        length: match[0].length,
        language: match[1] || '',
        code: match[2],
      });
    }
    
    // Process segments between code blocks
    codeBlockMatches.forEach((codeBlock) => {
      // Add text before code block
      if (codeBlock.index > currentIndex) {
        const textBefore = line.substring(currentIndex, codeBlock.index);
        tokens.push(...parseInlineFormatting(textBefore));
      }
      
      // Add code block token
      tokens.push({
        type: 'codeBlock',
        content: codeBlock.code,
        language: codeBlock.language,
      });
      
      currentIndex = codeBlock.index + codeBlock.length;
    });
    
    // Add remaining text after last code block
    if (currentIndex < line.length) {
      tokens.push(...parseInlineFormatting(line.substring(currentIndex)));
    }
    
    // If no code blocks, parse entire line
    if (codeBlockMatches.length === 0) {
      return parseInlineFormatting(line);
    }
    
    return tokens;
  };
  
  /**
   * Parse inline formatting (links, code, bold, italic, strikethrough)
   */
  const parseInlineFormatting = (text: string): Token[] => {
    const tokens: Token[] = [];
    let currentIndex = 0;
    
    // Collect all matches with their positions
    const matches: Array<{ index: number; length: number; type: TokenType; content: string; url?: string }> = [];
    
    // Links: <url|text> or <url>
    const linkRegex = /<([^>|]+)(?:\|([^>]+))?>/g;
    let match: RegExpExecArray | null;
    while ((match = linkRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: 'link',
        content: match[2] || match[1], // Use text if available, otherwise URL
        url: match[1],
      });
    }
    
    // Inline code: `code` (but not inside links)
    const codeRegex = /`([^`]+)`/g;
    match = null; // Reset for next regex
    while ((match = codeRegex.exec(text)) !== null) {
      // Check if this code is inside a link match
      const isInsideLink = matches.some(m => 
        m.type === 'link' && match !== null && match.index >= m.index && match.index < m.index + m.length
      );
      if (!isInsideLink) {
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'code',
          content: match[1],
        });
      }
    }
    
    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);
    
    // Process segments between matches
    matches.forEach((matchItem) => {
      // Add text before match
      if (matchItem.index > currentIndex) {
        const textBefore = text.substring(currentIndex, matchItem.index);
        tokens.push(...parseTextFormatting(textBefore));
      }
      
      // Add match token
      if (matchItem.type === 'link') {
        tokens.push({
          type: 'link',
          content: matchItem.content,
          url: matchItem.url,
        });
      } else if (matchItem.type === 'code') {
        tokens.push({
          type: 'code',
          content: matchItem.content,
        });
      }
      
      currentIndex = matchItem.index + matchItem.length;
    });
    
    // Add remaining text
    if (currentIndex < text.length) {
      tokens.push(...parseTextFormatting(text.substring(currentIndex)));
    }
    
    // If no matches, parse text formatting
    if (matches.length === 0) {
      return parseTextFormatting(text);
    }
    
    return tokens;
  };
  
  /**
   * Parse text formatting (bold, italic, strikethrough)
   * Process in order: bold > italic > strikethrough
   */
  const parseTextFormatting = (text: string): Token[] => {
    const tokens: Token[] = [];
    let currentIndex = 0;
    
    // Collect formatting matches
    const matches: Array<{ index: number; length: number; type: TokenType; content: string }> = [];
    
    // Bold: *text* (not inside code)
    const boldRegex = /\*([^*]+)\*/g;
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: 'bold',
        content: match[1],
      });
    }
    
    // Italic: _text_ (not inside code or bold)
    const italicRegex = /_([^_]+)_/g;
    match = null; // Reset for next regex
    while ((match = italicRegex.exec(text)) !== null) {
      // Check if inside bold
      const isInsideBold = matches.some(m => 
        m.type === 'bold' && match !== null && match.index >= m.index && match.index < m.index + m.length
      );
      if (!isInsideBold) {
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'italic',
          content: match[1],
        });
      }
    }
    
    // Strikethrough: ~text~ (not inside code, bold, or italic)
    const strikethroughRegex = /~([^~]+)~/g;
    match = null; // Reset for next regex
    while ((match = strikethroughRegex.exec(text)) !== null) {
      const isInsideOther = matches.some(m => 
        match !== null && match.index >= m.index && match.index < m.index + m.length
      );
      if (!isInsideOther) {
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'strikethrough',
          content: match[1],
        });
      }
    }
    
    // Sort by index
    matches.sort((a, b) => a.index - b.index);
    
    // Process segments
    matches.forEach((matchItem) => {
      if (matchItem.index > currentIndex) {
        tokens.push({
          type: 'text',
          content: text.substring(currentIndex, matchItem.index),
        });
      }
      
      tokens.push({
        type: matchItem.type,
        content: matchItem.content,
      });
      
      currentIndex = matchItem.index + matchItem.length;
    });
    
    // Add remaining text
    if (currentIndex < text.length) {
      tokens.push({
        type: 'text',
        content: text.substring(currentIndex),
      });
    }
    
    // If no matches, return plain text
    if (matches.length === 0) {
      return [{ type: 'text', content: text }];
    }
    
    return tokens;
  };
  
  /**
   * Render a token to React element
   */
  const renderToken = (token: Token, key: string, theme: any): React.ReactNode => {
    const codeBg = theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : '#f1f3f5';
    const codeColor = theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : '#000';
    switch (token.type) {
      case 'link':
        return (
          <Link
            key={key}
            href={token.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {renderTokens([{ type: 'text', content: token.content }], `${key}-content`, theme)}
          </Link>
        );
      
      case 'bold':
        return <strong key={key}>{token.content}</strong>;
      
      case 'italic':
        return <em key={key}>{token.content}</em>;
      
      case 'strikethrough':
        return <s key={key}>{token.content}</s>;
      
      case 'code':
        return (
          <code key={key} style={{ 
            backgroundColor: codeBg, 
            color: codeColor,
            padding: '2px 4px', 
            borderRadius: '3px', 
            fontFamily: 'monospace' 
          }}>
            {token.content}
          </code>
        );
      
      case 'codeBlock':
        return (
          <pre key={key} style={{ 
            backgroundColor: codeBg, 
            color: codeColor,
            padding: '12px', 
            borderRadius: '4px', 
            overflow: 'auto', 
            margin: '0.5em 0' 
          }}>
            <code>{token.content}</code>
          </pre>
        );
      
      case 'text':
      default:
        return <React.Fragment key={key}>{token.content}</React.Fragment>;
    }
  };
  
  /**
   * Render multiple tokens
   */
  const renderTokens = (tokens: Token[], keyPrefix: string, theme: any): React.ReactNode => {
    return (
      <>
        {tokens.map((token, idx) => renderToken(token, `${keyPrefix}-${idx}`, theme))}
      </>
    );
  };
  
  /**
   * Check if line is a list item
   */
  const isListItem = (line: string): { isList: boolean; marker: string; content: string } => {
    // Unordered list: • or - or *
    const unorderedMatch = line.match(/^(\s*)([•\-*])\s+(.+)$/);
    if (unorderedMatch) {
      return {
        isList: true,
        marker: unorderedMatch[2],
        content: unorderedMatch[3],
      };
    }
    
    // Ordered list: 1. or 1)
    const orderedMatch = line.match(/^(\s*)(\d+)[.)]\s+(.+)$/);
    if (orderedMatch) {
      return {
        isList: true,
        marker: `${orderedMatch[2]}.`,
        content: orderedMatch[3],
      };
    }
    
    return { isList: false, marker: '', content: line };
  };
  
  /**
   * Check if line is a blockquote
   */
  const isBlockquote = (line: string): boolean => {
    return line.trim().startsWith('>');
  };
  
  /**
   * Check if line is a horizontal rule
   */
  const isHorizontalRule = (line: string): boolean => {
    return /^─+$/.test(line.trim());
  };
  
  /**
   * Process content, handling multi-line structures
   */
  const processContent = (content: string, theme: any): React.ReactNode[] => {
    const codeBg = theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : '#f1f3f5';
    const codeColor = theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : '#000';
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeBlockLines: string[] = [];
    let listItems: React.ReactNode[] = [];
    let inList = false;
    
    lines.forEach((line, idx) => {
      // Handle code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          const codeContent = codeBlockLines.join('\n');
          elements.push(
            <pre key={`code-${idx}`} style={{ backgroundColor: codeBg, color: codeColor, padding: '12px', borderRadius: '4px', overflow: 'auto', margin: '0.5em 0' }}>
              <code>{codeContent}</code>
            </pre>
          );
          codeBlockLines = [];
          inCodeBlock = false;
        } else {
          // Start code block
          inCodeBlock = true;
        }
        return;
      }
      
      if (inCodeBlock) {
        codeBlockLines.push(line);
        return;
      }
      
      // Handle lists
      const listInfo = isListItem(line);
      if (listInfo.isList) {
        if (!inList) {
          // Start new list
          inList = true;
          listItems = [];
        }
        const tokens = parseLine(listInfo.content);
        listItems.push(
          <Box
            key={`li-${idx}`}
            component="li"
            sx={{
              margin: '0.25em 0',
              paddingLeft: '0.5em',
            }}
          >
            <span style={{ marginRight: '0.5em' }}>{listInfo.marker}</span>
            {renderTokens(tokens, `line-${idx}`, theme)}
          </Box>
        );
        return;
      } else {
        // End list if we were in one
        if (inList && listItems.length > 0) {
          elements.push(
            <Box key={`ul-${idx}`} component="ul" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
              {listItems}
            </Box>
          );
          listItems = [];
          inList = false;
        }
      }
      
      // Handle horizontal rules
      if (isHorizontalRule(line)) {
        elements.push(
          <hr key={`hr-${idx}`} style={{ margin: '1em 0', border: 'none', borderTop: `1px solid ${theme.palette.divider}` }} />
        );
        return;
      }
      
      // Handle blockquotes
      if (isBlockquote(line)) {
        const quoteContent = line.replace(/^>\s*/, '');
        const tokens = parseLine(quoteContent);
        elements.push(
          <Box
            key={`quote-${idx}`}
            component="blockquote"
            sx={{
              margin: '0.5em 0',
              paddingLeft: '1em',
              borderLeft: `3px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
            }}
          >
            {renderTokens(tokens, `line-${idx}`, theme)}
          </Box>
        );
        return;
      }
      
      // Regular line
      const tokens = parseLine(line);
      if (tokens.length > 0 && tokens.some(t => t.type !== 'text' || t.content.trim() !== '')) {
        elements.push(
          <Box key={`line-${idx}`} component="p" sx={{ margin: '0.5em 0' }}>
            {renderTokens(tokens, `line-${idx}`, theme)}
          </Box>
        );
      } else if (line.trim() === '') {
        // Empty line
        elements.push(<br key={`br-${idx}`} />);
      }
    });
    
    // Close any open list
    if (inList && listItems.length > 0) {
      elements.push(
        <Box key="ul-end" component="ul" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
          {listItems}
        </Box>
      );
    }
    
    return elements;
  };
  
  return <>{processContent(content, theme)}</>;
};

interface SlackPreviewProps {
  markdownContent: string;
}

export const SlackPreview: React.FC<SlackPreviewProps> = ({
  markdownContent,
}) => {
  const theme = useTheme();
  const [preview, setPreview] = useState<SlackPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!markdownContent.trim()) {
      setPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await previewSlack({
          markdown_content: markdownContent,
        });
        setPreview(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to preview');
        setPreview(null);
      } finally {
        setLoading(false);
      }
    };

    // Debounce preview requests
    const timeoutId = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [markdownContent]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Slack Preview
        </Typography>
        {preview && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Length: {preview.estimated_length} characters
            </Typography>
            {preview.will_split && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Message will be split into multiple parts (exceeds 4000 character limit)
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Paper
        sx={{
          flex: 1,
          p: 2,
          overflow: 'auto',
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading && <Typography>Loading preview...</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && preview && (
          <Box
            sx={{
              '& p': { margin: '0.5em 0', color: theme.palette.text.primary },
              '& h1, & h2, & h3': { 
                marginTop: '1em', 
                marginBottom: '0.5em', 
                fontWeight: 'bold',
                color: theme.palette.text.primary,
              },
              '& ul, & ol': { marginLeft: '1.5em', color: theme.palette.text.primary },
              '& code': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f1f3f5',
                color: theme.palette.text.primary,
                padding: '2px 4px',
                borderRadius: '3px',
                fontFamily: 'monospace',
              },
              '& pre': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f1f3f5',
                color: theme.palette.text.primary,
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
              },
              '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                '&:hover': {
                  color: theme.palette.primary.dark,
                },
              },
              whiteSpace: 'pre-wrap',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            <MrkdwnRenderer content={preview.mrkdwn_content} theme={theme} />
          </Box>
        )}
        {!loading && !error && !preview && markdownContent && (
          <Typography color="text.secondary">
            Preview will appear here as you type...
          </Typography>
        )}
        {!markdownContent && (
          <Typography color="text.secondary">
            Start typing to see Slack preview...
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
